// piano-frontend/src/components/MakeupBookingDialog.js
// 보강 레슨 잡기 — 특정 회원 프리셋으로 주간 레슨 표를 띄우고, 빈 칸 클릭으로 대리 예약(티켓 차감)한다.
// 고정제 자동 예약이 자리가 없어 미배정(스킵)된 경우 관리자가 회원과 협의한 일시에 바로 잡아주는 용도.
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, DialogTitle, DialogContent, DialogActions,
  Button, Chip, CircularProgress, IconButton, Stack, Alert,
  FormControlLabel, Checkbox, useMediaQuery, useTheme
} from "@mui/material";
import Dialog from "./AppDialog";
import { ChevronLeft, ChevronRight, Close, ConfirmationNumber } from "@mui/icons-material";
import api from "../api/axios";
import { useDarkMode } from "../App";

const KO_DAYS = ["일", "월", "화", "수", "목", "금", "토"];

function mondayOf(dateStr) {
  const base = dateStr ? new Date(dateStr) : new Date();
  const dow = base.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(base);
  monday.setDate(base.getDate() + offset);
  return monday.toISOString().split("T")[0];
}

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

function add30(timeStr) {
  const [h, m] = timeStr.split(":").map(Number);
  const total = h * 60 + m + 30;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

function dateLabel(dateStr) {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

// active: keep-alive 페이지가 숨겨질 때 함께 숨기기 위한 표시용 플래그.
// 의미상의 open과 분리되어 있어 숨김/복원 시 초기화 이펙트([open, initialDate])가 다시 돌지 않는다.
function MakeupBookingDialog({ open, active = true, onClose, member, initialDate, marks = [], onBooked }) {
  const theme = useTheme();
  const { darkMode } = useDarkMode();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [weekStart, setWeekStart] = useState(mondayOf(initialDate));
  const [data, setData] = useState({ teachers: [], lessons: [], overrides: {} });
  const [blockSchedules, setBlockSchedules] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [memberInfo, setMemberInfo] = useState(null);
  const [confirm, setConfirm] = useState(null); // { date, time, teacher }
  const [createPractice, setCreatePractice] = useState(true);
  const [booking, setBooking] = useState(false);
  const [notice, setNotice] = useState(null); // { severity, message }

  const weekDates = Array.from({ length: 6 }, (_, i) => addDays(weekStart, i));
  const todayStr = new Date().toISOString().split("T")[0];

  const fetchMemberInfo = useCallback(async () => {
    if (!member?.id) return;
    try {
      const res = await api.get(`/members/${member.id}`);
      setMemberInfo(res.data);
    } catch (err) {
      console.error("회원 정보 조회 실패:", err);
    }
  }, [member?.id]);

  const fetchWeek = useCallback(async (ws) => {
    setLoading(true);
    try {
      const teachersRes = await api.get("/teachers");
      const teachers = teachersRes.data;
      const [lessonsRes, overrideLists, holidayList] = await Promise.all([
        api.get("/lessons", { params: { week_start: ws } }),
        Promise.all(teachers.map((t) => api.get(`/teachers/${t.id}/schedule-overrides`).then((r) => r.data).catch(() => []))),
        api.get("/holidays", { params: { start_date: ws, end_date: addDays(ws, 5) } }).then((r) => r.data).catch(() => []),
      ]);
      const overrides = {};
      teachers.forEach((t, i) => { overrides[t.id] = overrideLists[i]; });
      setData({ teachers, lessons: lessonsRes.data, overrides });
      setHolidays(holidayList);
    } catch (err) {
      console.error("주간 스케줄 조회 실패:", err);
      setNotice({ severity: "error", message: "스케줄을 불러오지 못했습니다." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const ws = mondayOf(initialDate);
    setWeekStart(ws);
    setNotice(null);
    fetchWeek(ws);
    fetchMemberInfo();
    api.get("/block-schedules").then((res) => setBlockSchedules(res.data)).catch(() => setBlockSchedules([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialDate]);

  const moveWeek = (delta) => {
    const ws = addDays(weekStart, delta * 7);
    setWeekStart(ws);
    fetchWeek(ws);
  };

  // 해당 날짜의 유효 근무 시간대 (변동 스케줄 우선)
  const effectiveWorkWindows = (teacher, dateStr) => {
    const ov = (data.overrides?.[teacher.id] || []).find((o) => o.date === dateStr);
    if (ov) {
      if (ov.type === "OFF") return [];
      if (ov.type === "ADJUSTED" && ov.work_start && ov.work_end) return [{ start: ov.work_start, end: ov.work_end }];
    }
    const dow = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][new Date(dateStr).getDay()];
    return (teacher.schedules || []).filter((s) => s.day === dow).map((s) => ({ start: s.start, end: s.end }));
  };
  const worksAt = (teacher, dateStr, timeStr) =>
    effectiveWorkWindows(teacher, dateStr).some((w) => timeStr >= String(w.start).slice(0, 5) && timeStr < String(w.end).slice(0, 5));

  const isBlockedBySchedule = (teacherId, dateStr, timeStr) => {
    const dow = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][new Date(dateStr).getDay()];
    const bs = blockSchedules.find((b) => b.teacher_id === teacherId && b.day_of_week === dow && b.start_time.slice(0, 5) === timeStr);
    if (!bs) return false;
    const ov = bs.overrides?.find((o) => o.date === dateStr);
    if (ov && ov.is_open) return false;
    return true;
  };

  // 주간 전체 근무 시간대에서 30분 슬롯 목록 도출 (없으면 10:00~22:00)
  const timeSlots = (() => {
    let min = null;
    let max = null;
    for (const d of weekDates) {
      for (const t of data.teachers) {
        for (const w of effectiveWorkWindows(t, d)) {
          const s = String(w.start).slice(0, 5);
          const e = String(w.end).slice(0, 5);
          if (min === null || s < min) min = s;
          if (max === null || e > max) max = e;
        }
      }
    }
    if (!min || !max) { min = "10:00"; max = "22:00"; }
    const slots = [];
    let cur = min;
    while (cur < max) {
      slots.push(cur);
      cur = add30(cur);
    }
    return slots;
  })();

  const lessonsAt = (dateStr, teacherId, timeStr) =>
    data.lessons.filter((l) => l.date === dateStr && l.teacher_id === teacherId && l.start_time.slice(0, 5) === timeStr);

  const memberHasLessonAt = (dateStr, timeStr) =>
    data.lessons.some((l) => l.date === dateStr && l.user_id === member?.id && l.start_time.slice(0, 5) === timeStr);

  const isMarked = (dateStr, timeStr, teacherId) =>
    marks.some((m) => m.date === dateStr && String(m.start_time).slice(0, 5) === timeStr && (m.teacher_id == null || m.teacher_id === teacherId));

  const isHoliday = (dateStr) => holidays.some((h) => h.date === dateStr);

  const handleBook = async (confirmHoliday) => {
    if (!confirm) return;
    setBooking(true);
    try {
      await api.post("/lessons", {
        user_id: member.id,
        teacher_id: confirm.teacher.id,
        date: confirm.date,
        start_time: confirm.time,
        end_time: add30(confirm.time),
        skip_practice: !createPractice,
        ...(confirmHoliday === true ? { confirm_holiday: true } : {}),
      });
      setNotice({ severity: "success", message: `✅ ${dateLabel(confirm.date)} ${confirm.time} · ${confirm.teacher.name} 선생님으로 예약했습니다. (티켓 1장 차감)` });
      setConfirm(null);
      fetchWeek(weekStart);
      fetchMemberInfo();
      onBooked && onBooked();
    } catch (err) {
      // 표를 불러온 뒤에 휴일로 지정된 날짜: 서버가 확인 플래그를 요구하므로 같은 다이얼로그에서 재확인 받는다
      if (err?.response?.status === 409 && err?.response?.data?.code === "HOLIDAY_CONFIRM_REQUIRED") {
        setConfirm((c) => (c ? { ...c, holidayWarn: true } : c));
      } else {
        setNotice({ severity: "error", message: err?.response?.data?.message || "예약에 실패했습니다." });
        setConfirm(null);
      }
    } finally {
      setBooking(false);
    }
  };

  const remaining = memberInfo?.ticket ?? null;

  const cellBg = {
    available: darkMode ? "rgba(76, 175, 80, 0.16)" : "#e8f5e9",
    availableBorder: darkMode ? "rgba(76, 175, 80, 0.5)" : "#a5d6a7",
    booked: darkMode ? "rgba(255, 167, 38, 0.16)" : "#fff3e0",
    block: darkMode ? "rgba(255,255,255,0.13)" : "#e8eaed",
    own: darkMode ? "rgba(33, 150, 243, 0.2)" : "#e3f2fd",
    mark: "#8e24aa",
  };

  return (
    <Dialog open={open && active} onClose={onClose} fullScreen={isMobile} maxWidth={false} fullWidth
      PaperProps={{ sx: isMobile ? {} : { height: "92vh", maxWidth: 1280 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
        <Typography variant="h6" fontWeight={700} component="span">
          보강 레슨 잡기
        </Typography>
        <Chip size="small" color="primary" label={member?.name || ""} />
        {remaining !== null && (
          <Chip
            size="small"
            icon={<ConfirmationNumber sx={{ fontSize: 16 }} />}
            color={remaining > 0 ? "success" : "default"}
            variant="outlined"
            label={`보유 티켓 ${remaining}장`}
          />
        )}
        <Box flex={1} />
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 1, p: { xs: 1, sm: 2 } }}>
        {notice && (
          <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ py: 0.25 }}>
            {notice.message}
          </Alert>
        )}
        {remaining === 0 && (
          <Alert severity="warning" sx={{ py: 0.25 }}>
            보유 티켓이 없습니다. 예약하면 티켓이 음수가 될 수 있으니 필요 시 티켓을 먼저 발행해주세요.
          </Alert>
        )}

        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => moveWeek(-1)}><ChevronLeft /></IconButton>
          <Typography fontWeight={600}>
            {dateLabel(weekDates[0])} ~ {dateLabel(weekDates[5])}
          </Typography>
          <IconButton size="small" onClick={() => moveWeek(1)}><ChevronRight /></IconButton>
          <Box flex={1} />
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
            빈 칸(예약 가능)을 클릭하면 해당 회원으로 예약합니다 · 티켓 1장 차감
          </Typography>
        </Stack>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" flex={1} py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ overflow: "auto", flex: 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `56px repeat(${weekDates.length}, minmax(110px, 1fr))`,
                gridAutoRows: "minmax(52px, auto)",
                minWidth: 720,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {/* 헤더 */}
              <Box sx={{ p: 0.5, bgcolor: darkMode ? "grey.900" : "grey.100", borderBottom: 1, borderColor: "divider" }} />
              {weekDates.map((d) => (
                <Box key={d} sx={{
                  p: 0.5, textAlign: "center", borderBottom: 1, borderLeft: 1, borderColor: "divider",
                  bgcolor: isHoliday(d)
                    ? (darkMode ? "#1a1a1a" : "#222")
                    : d === todayStr ? (darkMode ? "rgba(25,118,210,0.25)" : "#e3f2fd") : (darkMode ? "grey.900" : "grey.100"),
                }}>
                  <Typography variant="caption" fontWeight={700} sx={isHoliday(d) ? { color: "#f44336" } : undefined}>
                    {dateLabel(d)} ({KO_DAYS[new Date(d).getDay()]}){isHoliday(d) ? " · 휴일" : ""}
                  </Typography>
                </Box>
              ))}

              {/* 시간 행 */}
              {timeSlots.map((time) => (
                <React.Fragment key={time}>
                  <Box sx={{
                    p: 0.5, display: "flex", alignItems: "center", justifyContent: "center",
                    borderBottom: 1, borderColor: "divider", bgcolor: darkMode ? "grey.900" : "grey.50",
                  }}>
                    <Typography variant="caption" fontWeight={600}>{time}</Typography>
                  </Box>
                  {weekDates.map((d) => {
                    // 휴일: 시간표(AdminLessonPage)와 동일하게 컬럼 전체를 차단 표시 — 클릭 불가
                    if (isHoliday(d)) {
                      return (
                        <Box key={`${d}-${time}`} sx={{
                          p: 0.4, display: "flex", alignItems: "center", justifyContent: "center",
                          borderBottom: 1, borderLeft: 1, borderColor: "divider",
                          bgcolor: darkMode ? "#1a1a1a" : "#222",
                        }}>
                          <Typography sx={{ color: "#f44336", fontWeight: 700, fontSize: "0.72rem" }}>휴일</Typography>
                        </Box>
                      );
                    }
                    const workingTeachers = data.teachers.filter((t) => worksAt(t, d, time));
                    const ownHere = memberHasLessonAt(d, time);
                    const isPast = d < todayStr;
                    return (
                      <Box key={`${d}-${time}`} sx={{
                        p: 0.4, display: "flex", flexDirection: "column", gap: 0.4,
                        borderBottom: 1, borderLeft: 1, borderColor: "divider",
                        opacity: isPast ? 0.45 : 1,
                      }}>
                        {workingTeachers.map((t) => {
                          const slotLessons = lessonsAt(d, t.id, time);
                          const isBlockLesson = slotLessons.some((l) => l.user?.role === "ADMIN" || l.user?.role === "TEACHER");
                          const blocked = isBlockLesson || (slotLessons.length === 0 && isBlockedBySchedule(t.id, d, time));
                          const ownInSlot = slotLessons.some((l) => l.user_id === member?.id);
                          // 선생님 한 명은 같은 시간에 레슨 1건만 가능 (DB 유니크 제약)
                          const occupants = [...new Set(slotLessons.filter((l) => l.user?.role === "MEMBER" || !l.user?.role).map((l) => l.user_id))];
                          const full = occupants.length >= 1;
                          const marked = isMarked(d, time, t.id);
                          const clickable = !isPast && !blocked && !full && !ownHere;

                          let label; let bg; let border = "transparent"; let fg;
                          if (ownInSlot) {
                            label = `${member?.name} · 예약됨`;
                            bg = cellBg.own; fg = darkMode ? "#90caf9" : "#1565c0";
                          } else if (blocked) {
                            label = "블록";
                            bg = cellBg.block; fg = "text.disabled";
                          } else if (full) {
                            label = `마감 · ${slotLessons.map((l) => l.user?.name).filter(Boolean).join(", ")}`;
                            bg = cellBg.booked; fg = darkMode ? "#ffcc80" : "#e65100";
                          } else {
                            label = `${t.name} · 예약 가능`;
                            bg = cellBg.available; fg = darkMode ? "#a5d6a7" : "#2e7d32";
                            border = cellBg.availableBorder;
                          }

                          return (
                            <Box
                              key={t.id}
                              onClick={clickable ? () => setConfirm({ date: d, time, teacher: t }) : undefined}
                              sx={{
                                px: 0.75, py: 0.4, borderRadius: 0.75, fontSize: "0.72rem", fontWeight: 600,
                                bgcolor: bg, color: fg,
                                border: marked ? `2px solid ${cellBg.mark}` : `1px solid ${border}`,
                                cursor: clickable ? "pointer" : "default",
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                "&:hover": clickable ? { filter: "brightness(0.93)" } : {},
                              }}
                              title={marked ? "자동 예약이 미배정된 슬롯" : undefined}
                            >
                              {marked ? `⚑ ${label}` : label}
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })}
                </React.Fragment>
              ))}
            </Box>

            {/* 범례 */}
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap mt={1}>
              {[
                { t: "예약 가능 (클릭)", bg: cellBg.available },
                { t: "마감(예약 있음)", bg: cellBg.booked },
                { t: "블록", bg: cellBg.block },
                { t: "본인 예약", bg: cellBg.own },
                { t: "휴일 (예약 불가)", bg: darkMode ? "#1a1a1a" : "#222" },
              ].map((l) => (
                <Stack key={l.t} direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: l.bg, border: 1, borderColor: "divider" }} />
                  <Typography variant="caption" color="text.secondary">{l.t}</Typography>
                </Stack>
              ))}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, border: `2px solid ${cellBg.mark}` }} />
                <Typography variant="caption" color="text.secondary">미배정 슬롯 (⚑)</Typography>
              </Stack>
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>

      {/* 예약 확인 다이얼로그 */}
      <Dialog open={!!confirm && active} onClose={() => !booking && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>보강 레슨 예약</DialogTitle>
        <DialogContent>
          {confirm && (
            <Stack spacing={1.5}>
              <Typography>
                <b>{member?.name}</b> 회원을 아래 일시로 예약합니다.
              </Typography>
              <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                📅 {confirm.date} ({KO_DAYS[new Date(confirm.date).getDay()]}) {confirm.time}~{add30(confirm.time)}
                <br />👩‍🏫 {confirm.teacher.name} 선생님
              </Alert>
              {confirm.holidayWarn && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  이 날짜는 <b>휴일</b>로 지정되어 있습니다. 휴일에 예약하려면 아래 버튼으로 한 번 더 확인해주세요.
                </Alert>
              )}
              <FormControlLabel
                control={<Checkbox checked={createPractice} onChange={(e) => setCreatePractice(e.target.checked)} />}
                label="연습실 자동 배정"
              />
              <Typography variant="body2" color="text.secondary">
                티켓 1장이 차감됩니다. (현재 {remaining ?? "-"}장 → {remaining !== null ? remaining - 1 : "-"}장)
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={booking}>취소</Button>
          <Button
            variant="contained"
            color={confirm?.holidayWarn ? "warning" : "primary"}
            onClick={() => handleBook(confirm?.holidayWarn === true)}
            disabled={booking}
          >
            {booking ? "예약 중..." : confirm?.holidayWarn ? "휴일 예약 진행" : "예약"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default MakeupBookingDialog;
